import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ASM_ENABLED_LOCAL_STORAGE_KEY, AsmAuthStorageService, CsAgentAuthService, TokenTarget } from '@spartacus/asm/root';
import {
  AuthStorageService,
  BaseSiteService,
  CSRFResponse, EventService,
  FeatureConfigService,
  getLastValueSync,
  GlobalMessageService,
  GlobalMessageType, LogoutEvent,
  OAUTH_REDIRECT_FLOW_KEY,
  StorageSyncType,
  WindowRef
} from '@spartacus/core';
import { LoginFormComponentService } from '@spartacus/user/account/components';
import { combineLatest, from, Observable, of, withLatestFrom } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { getStorage, persistToStorage, readFromStorage } from 'src/app/shared/utils/browser-storage';
import { VscaAuthService } from 'src/core/auth/user-auth/facade/vsca-auth.service';

@Injectable()
export class VscaLoginFormComponentService extends LoginFormComponentService {
  private readonly baseSiteService = inject(BaseSiteService);
  private readonly featureConfig = inject(FeatureConfigService);
  private readonly csAgentAuthService = inject(CsAgentAuthService);
  protected authStorageService = inject(AuthStorageService);
  private readonly eventService = inject(EventService);
  protected storage: Storage;

  private readonly FLOW_KEY = 'vsca_oauth_flow_ts';
  /** Maximum ms to wait for the hybris OAuth redirect before the flag expires. */
  private readonly FLOW_TTL_MS = 60_000;

  constructor(
    protected auth: VscaAuthService,
    protected globalMessage: GlobalMessageService,
    protected winRef: WindowRef
  ) {
    super(auth, globalMessage, winRef);

    this.storage = getStorage(StorageSyncType.LOCAL_STORAGE, this.winRef);
    this.eventService.get(LogoutEvent).subscribe(() => this.clearOauthRedirectFlowFlag());

    if (this.featureConfig.isEnabled('authorizationCodeFlowByDefault')) {
      this.getCsrfToken();
      this.initCustomLogin();
      this.initBaseSiteIdInForm();
    }
  }

  form: FormGroup = new FormGroup({
    userId: new FormControl('', [Validators.required]),
    password: new FormControl('', Validators.required)
  });

  login(nativeForm?: HTMLFormElement): void {
    this.form.get('csrf')?.setValue('csrf');

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.featureConfig.isEnabled('authorizationCodeFlowByDefault') && nativeForm) {
      this.setOauthRedirectFlowFlag();
      this.getCsrfAndSubmit();
      this.busy$.next(true);
    } else {
      this.busy$.next(true);

      from(
        this.auth.loginWithCredentials(
          // TODO: consider dropping toLowerCase as this should not be part of the UI,
          // as it's too opinionated and doesn't work with other AUTH services
          this.form.value.userId.toLowerCase(),
          this.form.value.password
        )
      )
        .pipe(
          withLatestFrom(this.auth.isUserLoggedIn()),
          tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn))
        )
        .subscribe();
    }
  }

  authorize(): void {
    if (!this.featureConfig.isEnabled('authorizationCodeFlowByDefault')) {
      return;
    }

    const stored = readFromStorage<string>(
      this.storage as Storage,
      this.FLOW_KEY
    );

    const age = Date.now() - parseInt(stored, 10);

    if (stored && age < this.FLOW_TTL_MS) {
      return;
    }

    persistToStorage(this.FLOW_KEY, Date.now().toString() satisfies string, this.storage as Storage);

    this.auth.isUserLoggedIn().pipe(
      take(1),
    ).subscribe((isLoggedIn) => {
      if (isLoggedIn || this.storage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY)) {
        this.clearOauthRedirectFlowFlag();
        return;
      }

      this.auth.loginWithRedirect();
    });

    return;
  }

  clearOauthRedirectFlowFlag(): void {
    if (this.winRef.isBrowser()) {
      this.winRef.localStorage?.removeItem(OAUTH_REDIRECT_FLOW_KEY);
      this.winRef.localStorage?.removeItem(this.FLOW_KEY);
    }
  }

  protected setOauthRedirectFlowFlag(): void {
    if (this.winRef.isBrowser()) {
      this.winRef.localStorage?.setItem(OAUTH_REDIRECT_FLOW_KEY, 'true');
    }
  }

  private initBaseSiteIdInForm(): void {
    this.form.addControl('baseSiteId', new FormControl(getLastValueSync(this.baseSiteService.getActive()), Validators.required));
  }

  /**
   * Submits credentials as a native form POST so the browser follows the
   * authorizationserver's 302 redirect back to Spartacus with the auth code.
   * HttpClient cannot be used here because it does not follow cross-origin redirects.
   */
  private submitCredentialsForm(csrf: CSRFResponse): void {
    const form = this.winRef.document.createElement('form');
    form.method = this.method;
    form.action = this.action;
    const username = (this.form.value.userId as string).toLowerCase();
    const password = this.form.value.password as string;
    const baseSiteId = this.form.value.baseSiteId as string;

    const fields: Record<string, string> = {
      username,
      password,
      [csrf.parameterName || '_csrf']: csrf.token,
      baseSiteId,
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = this.winRef.document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    this.winRef.document.body.appendChild(form);
    this.winRef.localStorage?.setItem(OAUTH_REDIRECT_FLOW_KEY, 'true');
    form.submit();
    this.busy$.next(true);
  }

  private getCsrfAndSubmit(): void {
    if (this.csrf?.token) {
      this.submitCredentialsForm(this.csrf);
      return;
    }

    this.getCsrfToken(true);
  }

  private isAsmAuthStorageService(
    service: AuthStorageService | AsmAuthStorageService
  ): service is AsmAuthStorageService {
    return 'getTokenTarget' in service;
  }

  getCsrfToken(showWarning = false): void {
    if (
      !this.authConfigService.customLoginEnabled() ||
      !this.winRef.isBrowser() ||
      (getLastValueSync(this.auth.isUserLoggedIn()) && !this.storage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY))
    ) {
      // disable guard when custom login page is not enabled or when the application is running in SSR mode
      // or user is logged in and asm is not turned on.
      return;
    }

    this.auth.getCsrfToken().pipe(
      tap((token) => {
        this.csrfStateService.set(token);
      }),
      take(1),
      map(() => true),
      catchError(() => {
        if (showWarning) {
          const isASMEnabled: boolean = Boolean(this.storage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY));
          const isAsmAgentLoggedIn: boolean = getLastValueSync(this.isAsmUserLoggedIn());
          this.globalMessage.add({ key: isASMEnabled || isAsmAgentLoggedIn ? 'customLoginPage.userCantLoginIfAsmEnabled' : 'customLoginPage.authExpired' }, GlobalMessageType.MSG_TYPE_ERROR);
        }

        return of(true);
      })
    ).subscribe();
  }

  authorizeCustomerSupportAgentWhenUseCodeFlow(): void {
    if (!this.isAsmAuthStorageService(this.authStorageService) || !this.storage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY)) {
      return;
    }

    this.authStorageService.getTokenTarget()
      .pipe(
        take(1),
        map((tokenTarget) => (tokenTarget === TokenTarget.CSAgent)),
        filter(isTheSameTarget => isTheSameTarget === false)
      ).subscribe(() => this.csAgentAuthService.authorizeCustomerSupportAgentWhenUseCodeFlow());
  }

  private isAsmUserLoggedIn(): Observable<boolean> {
    let stream$ = of(false);
    if (this.isAsmAuthStorageService(this.authStorageService)) {
      stream$ = combineLatest([
        this.authStorageService.getToken(),
        this.authStorageService.getTokenTarget(),
      ]).pipe(
        take(1),
        map(([currentToken, tokenTarget]) => {
          return (
            !!currentToken?.access_token && tokenTarget === TokenTarget.CSAgent
          );
        }),
      );
    }

    return stream$;
  }
}
